<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faCircleCheck, faSignature } from '@fortawesome/free-solid-svg-icons'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { decodeUuidBase58FromLink } from './uuid-link-code'

    // Página pública de assinatura — a "assinatura" aqui é a confirmação de identidade (CPF +
    // geolocalização + metadados do navegador) que autoriza a assinatura criptográfica feita pela
    // lambda-sign no backend; não é o pad de desenho usado nos blocos de formulário (sign-view.svelte).
    type Step = 'idle' | 'locating' | 'confirming' | 'done'

    let show = true
    let error = ''
    let data: { signer: any; form: any; signers: any[]; consentText?: string } | null = null

    let step: Step = 'idle'
    let document_ = ''
    let geolocation: { latitude: number; longitude: number; accuracy: number } | null = null
    let submitting = false
    let alreadySigned = false

    let formV2FormSignatureId = ''
    let token = ''

    onMount(load)

    async function load() {
        try {
            show = true
            const urlParams = new URLSearchParams(window.location.search)
            // os dois chegam em base58 (ver signLinkQuery em form-v2-sign-popover.svelte); link antigo,
            // com uuid cru, continua valendo — inclusive o que o tools-api manda por e-mail
            formV2FormSignatureId = decodeUuidBase58FromLink(urlParams.get('id')) || ''
            // "t", não "token": "token" já é usado em toda a SPA para o JWT da sessão logada, e um
            // usuário autenticado abrindo este link público colidia com esse outro parâmetro.
            token = decodeUuidBase58FromLink(urlParams.get('t')) || ''
            if (!formV2FormSignatureId || !token) {
                error = 'Link inválido.'
                return
            }
            data = await window.api.tools.formsV2.formSignatures.publicGetOne(formV2FormSignatureId, token)
            if (data.signer.signedAt) {
                step = 'done'
                alreadySigned = true
            }
        } catch (err) {
            error = 'Este link não está mais disponível.'
        } finally {
            show = false
        }
    }

    function startSign() {
        step = 'locating'
        if (!navigator.geolocation) {
            geolocation = null
            step = 'confirming'
            return
        }
        // Sem timeout, de propósito — só avança pra tela de assinatura quando o usuário de fato
        // responder ao prompt de localização (autorizando ou negando), nunca por expiração.
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                geolocation = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }
                step = 'confirming'
            },
            () => {
                geolocation = null
                step = 'confirming'
            },
        )
    }

    async function submitSign() {
        const sanitized = window.api.utils.utils.sanitizeDocument(document_)
        if (sanitized.length < 11) {
            window.error('Documento inválido')
            return
        }
        if (!window.api.utils.utils.validateCNPJ(sanitized) && !window.api.utils.utils.validateCPF(sanitized)) {
            window.error('Documento inválido')
            return
        }
        try {
            submitting = true
            const result = await window.api.tools.formsV2.formSignatures.publicSign(formV2FormSignatureId, token, {
                document: sanitized,
                geolocation,
            })
            alreadySigned = !!result?.alreadySigned
            step = 'done'
            // reassinatura do mesmo link não conta duas vezes
            if (!result?.alreadySigned) window.api.users.statistics.tickPublic(data?.form?.companyId, 'pub.form.sign')
            // Sem novo GET aqui de propósito: uma vez que todos assinam, o backend invalida os tokens
            // de link (inclusive o de quem acabou de assinar) — um publicGetOne agora poderia falhar.
            // Atualiza o estado local com o que já sabemos, sem depender de outra chamada autenticada.
            if (data && !result?.alreadySigned) {
                const signedAt = new Date().toISOString()
                data = {
                    ...data,
                    signer: { ...data.signer, signedAt },
                    signers: data.signers.map((s) => (s.personName === data.signer.personName ? { ...s, signedAt } : s)),
                }
            }
        } catch (err) {
            window.error(err)
        } finally {
            submitting = false
        }
    }

    function signerStatusLabel(signer: any) {
        return signer.signedAt ? 'Assinado' : 'Pendente'
    }

    // Parâmetros do visualizador de PDF do navegador: esconde o painel lateral de miniaturas e abre
    // o documento já ajustado à largura. Vão no fragmento (#), não na query — a URL do S3 vem
    // assinada, com query própria, e qualquer parâmetro extra ali invalidaria a assinatura.
    $: previewUrl = data?.form?.signedDocumentUrl ? data.form.signedDocumentUrl + '#navpanes=0&view=FitH' : ''
</script>

<div class="root">
    <LoadSpinner {show} />
    {#if error}
        <div class="msg">{error}</div>
    {:else if data}
        <div class="sign-grid">
            <!-- Assinantes e caixa de assinatura na mesma coluna, à esquerda da prévia. No mobile a
                 grade vira uma coluna só e esta div, por vir antes no DOM, fica acima do documento. -->
            <div class="sign-side">
                <div class="sign-signers">
                    <div class="sign-signers-title">Assinantes</div>
                    {#each data.signers as signer}
                        <div class="signer-row">
                            <div class="signer-name">{signer.personName}</div>
                            <div class="signer-status" class:signed={!!signer.signedAt}>{signerStatusLabel(signer)}</div>
                        </div>
                    {/each}
                </div>

                <div class="sign-action">
                    {#if step === 'done'}
                        <div class="success">
                            <Fa icon={faCircleCheck} />
                            <span>{alreadySigned ? 'Você já assinou este documento.' : 'Assinatura concluída com sucesso!'}</span>
                        </div>
                    {:else if step === 'idle'}
                        <button class="sign-btn" on:click={startSign}>
                            <Fa icon={faSignature} />
                            <span>Assinar</span>
                        </button>
                        <!-- Só no mobile: ali o documento fica abaixo da caixa de assinatura, fora da
                             primeira tela, então vale deixar explícito que ele está disponível para
                             leitura antes de assinar. No desktop ele está lado a lado, à vista. -->
                        <p class="doc-hint">O documento completo está logo abaixo, nesta mesma tela, para leitura antes de assinar.</p>
                    {:else if step === 'locating'}
                        <div class="locating">Aguardando localização...</div>
                    {:else if step === 'confirming'}
                        <div class="confirm-form">
                            <div class="field">
                                <label for="sign-document">CPF</label>
                                <input id="sign-document" type="text" inputmode="numeric" bind:value={document_} placeholder="000.000.000-00" />
                            </div>
                            <div class="field">
                                <label for="sign-key">ID único</label>
                                <input id="sign-key" type="text" value={data.signer.friendlySignatureKey} disabled />
                            </div>
                            <!-- Aceite expresso: é aqui que o consentimento é efetivamente colhido, junto
                                 com as evidências gravadas em FormV2FormSignatureLog. O texto vem do backend
                                 (SIGNATURE_CONSENT_TEXT) e é o mesmo que fica gravado no log ao assinar —
                                 escrevê-lo aqui abriria espaço pra exibição e prova divergirem. -->
                            {#if data.consentText}
                                <p class="consent-text">{data.consentText}</p>
                            {/if}
                            <button class="sign-btn" disabled={submitting} on:click={submitSign}>
                                <Fa icon={faSignature} />
                                <span>{submitting ? 'Assinando...' : 'Concordo'}</span>
                            </button>
                        </div>
                    {/if}
                </div>
            </div>

            <div class="sign-preview">
                {#if previewUrl}
                    <iframe title="Documento" src={previewUrl} />
                {:else}
                    <div class="msg">O documento ainda está sendo preparado.</div>
                {/if}
            </div>
        </div>
    {/if}
</div>

<style>
    .root {
        min-height: 100vh;
        background: #f4f5f7;
        color: #212529;
        padding: 16px;
    }
    .msg {
        max-width: 700px;
        margin: 40px auto;
        text-align: center;
        color: #495057;
    }
    .sign-grid {
        display: grid;
        /* Um pouco mais larga que os 320px de antes: a coluna passou a abrigar também a caixa de
           assinatura, cujo texto de aceite é um parágrafo e fica ruim de ler muito estreito. */
        grid-template-columns: 380px 1fr;
        gap: 16px;
        max-width: 1200px;
        margin: 0 auto;
    }
    .sign-side {
        display: flex;
        flex-direction: column;
        gap: 16px;
        /* Não estica junto com a prévia do documento, que é bem mais alta. */
        align-self: start;
    }
    @media (max-width: 900px) {
        .sign-grid {
            grid-template-columns: 1fr;
        }
        .doc-hint {
            display: block;
        }
    }
    .sign-signers {
        background: #fff;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        height: fit-content;
    }
    .sign-signers-title {
        font-weight: 600;
        margin-bottom: 12px;
    }
    .signer-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 0px;
        border-bottom: 1px solid #eee;
    }
    .signer-status {
        font-size: 0.85rem;
        color: #868e96;
    }
    .signer-status.signed {
        color: #2f9e44;
        font-weight: 600;
    }
    .sign-preview {
        background: #fff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        min-height: calc(100vh - 40px);
        display: flex;
    }
    .sign-preview iframe {
        width: 100%;
        height: 100%;
        border: 0px;
    }
    @media (max-width: 900px) {
        .sign-preview {
            min-height: unset;
            height: 40vh;
        }
        .sign-preview iframe {
            min-height: unset;
        }
    }
    .sign-action {
        background: #fff;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }
    /* Só faz sentido no mobile, onde o documento fica abaixo desta caixa (ver comentário no markup). */
    .doc-hint {
        display: none;
        margin: 0;
        font-size: 0.75rem;
        color: #868e96;
        text-align: center;
    }
    .sign-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: none;
        border-radius: 8px;
        padding: 12px 28px;
        font-size: 1rem;
        font-weight: 600;
        background: #4263eb;
        color: #fff;
        cursor: pointer;
    }
    .sign-btn:disabled {
        opacity: 0.6;
        cursor: default;
    }
    .locating {
        color: #495057;
        font-weight: 500;
    }
    .confirm-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        max-width: 360px;
    }
    .field {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .field label {
        font-size: 0.8rem;
        color: #868e96;
    }
    .field input {
        padding: 8px 10px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 0.95rem;
    }
    .field input:disabled {
        background: #f1f3f5 !important;
        color: #868e96 !important;
        border-color: #ced4da !important;
    }
    .consent-text {
        margin: 0;
        font-size: 0.76rem;
        line-height: 1.45;
        color: #495057;
        text-align: justify;
    }
    .success {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #2f9e44;
        font-weight: 600;
        font-size: 1.05rem;
    }
</style>
